import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { APP } from "@/constants/testIds";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from "recharts";
import { Loader2 } from "lucide-react";

export default function HealthScore() {
  const [dims, setDims] = useState([]);
  const [ratings, setRatings] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [latest, setLatest] = useState(null);

  useEffect(() => {
    (async () => {
      const [d, l] = await Promise.all([api.get("/health-score/dimensions"), api.get("/health-score/latest")]);
      setDims(d.data.dimensions);
      const seed = {};
      d.data.dimensions.forEach((x) => (seed[x] = 5));
      if (l.data.score) {
        setLatest(l.data.score);
        Object.assign(seed, l.data.score.scores || {});
      }
      setRatings(seed);
    })();
  }, []);

  const composite = Object.values(ratings).length
    ? Math.round((Object.values(ratings).reduce((a, b) => a + Number(b), 0) / Object.values(ratings).length) * 10)
    : 0;

  const chartData = dims.map((d) => ({ dimension: d, value: Number(ratings[d] || 0) }));

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/health-score/generate", { ratings });
      setResult(data.score);
      setLatest(data.score);
      toast.success("Business Health Report generated and saved to your Assets Library.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="label-mono mb-2">AI Module</div>
        <h1 className="font-display text-4xl font-bold tracking-tight text-slate-950">Business Health Score™</h1>
        <p className="mt-2 text-sm text-stone-600">Rate yourself across 15 dimensions. Our AI produces your composite score and 90-day improvement plan.</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
        {/* Ratings panel */}
        <div className="card-flat">
          <div className="label-mono mb-3">Self-Assessment (0–10)</div>
          <div className="space-y-4 max-h-[520px] overflow-y-auto pr-2">
            {dims.map((d) => (
              <div key={d}>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-950">{d}</label>
                  <div className="font-mono text-sm text-slate-950">{ratings[d] ?? 0}</div>
                </div>
                <input
                  type="range" min={0} max={10} step={1}
                  value={ratings[d] ?? 0}
                  onChange={(e) => setRatings({ ...ratings, [d]: Number(e.target.value) })}
                  className="mt-2 w-full accent-slate-950"
                />
              </div>
            ))}
          </div>
          <button data-testid={APP.healthGenerate} onClick={generate} disabled={loading} className="btn-primary mt-6 w-full">
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating Report…</> : "Generate AI Analysis"}
          </button>
        </div>

        {/* Radar + Composite */}
        <div className="card-flat">
          <div className="flex items-start justify-between">
            <div>
              <div className="label-mono">Composite Score</div>
              <div className="mt-2 font-display text-6xl font-bold text-slate-950">{composite}<span className="text-2xl text-stone-400">/100</span></div>
            </div>
            {latest && (
              <div className="text-right">
                <div className="label-mono !text-[9px]">Last Report</div>
                <div className="mt-1 font-mono text-xs text-stone-600">{new Date(latest.created_at).toLocaleDateString()}</div>
              </div>
            )}
          </div>
          <div className="mt-4 h-[380px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={chartData}>
                <PolarGrid stroke="#e7e5e4" />
                <PolarAngleAxis dataKey="dimension" tick={{ fill: "#475569", fontSize: 10 }} />
                <PolarRadiusAxis angle={90} domain={[0, 10]} tick={{ fill: "#94a3b8", fontSize: 9 }} />
                <Radar name="You" dataKey="value" stroke="#0f172a" fill="#0f172a" fillOpacity={0.15} strokeWidth={2} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Analysis */}
      {(result?.analysis || latest?.analysis) && (
        <div className="card-flat">
          <div className="label-mono mb-3">AI Analysis</div>
          <div className="prose prose-sm prose-stone max-w-none whitespace-pre-wrap text-sm leading-relaxed text-slate-900">
            {(result?.analysis || latest?.analysis)}
          </div>
        </div>
      )}
    </div>
  );
}
